import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton, SecondaryButton, DangerButton } from "./Buttons.jsx";
import { addToast, checkSession } from "../store/app/action.js";
import { FaShield, FaKey, FaTriangleExclamation, FaFingerprint } from "react-icons/fa6";
import { deleteTOTP } from "../api/auth.js";
import TOTPSetupModal from "./TOTPSetupModal.jsx";
import BackupCodesModal from "./BackupCodesModal.jsx";
import WebAuthnSetupModal from "./WebAuthnSetupModal.jsx";
import WebAuthnManageModal from "./WebAuthnManageModal.jsx";
import ModalBasic from "./ModalBasic.jsx";

function MFASection({ showTitle = true, className = "" }) {
  const { user } = useSelector((state) => state.app);
  const dispatch = useDispatch();
  const [totpModalOpen, setTotpModalOpen] = useState(false);
  const [backupCodesModalOpen, setBackupCodesModalOpen] = useState(false);
  const [webauthnModalOpen, setWebauthnModalOpen] = useState(false);
  const [webauthnManageModalOpen, setWebauthnManageModalOpen] = useState(false);
  const [deletingTOTP, setDeletingTOTP] = useState(false);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

  const handleDeleteTOTP = () => {
    setConfirmDeleteModalOpen(true);
  };

  const handleManageWebauthn = () => {
    setWebauthnManageModalOpen(true);
  };

  const confirmDeleteTOTP = async () => {
    setConfirmDeleteModalOpen(false);
    setDeletingTOTP(true);
    try {
      await deleteTOTP();
      dispatch(
        addToast({
          type: "success",
          message: "Authenticator app setup deleted successfully",
          timer: 3,
        })
      );
      // Refresh user data to get updated MFA status
      dispatch(checkSession());
    } catch (error) {
      dispatch(
        addToast({
          type: "error",
          message: "Failed to delete authenticator app setup. Please try again.",
          timer: 5,
        })
      );
    } finally {
      setDeletingTOTP(false);
    }
  };

  // Handle case where user is null
  if (!user) {
    return (
      <div className={className}>
        {showTitle && (
          <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
            Multi-Factor Authentication
          </h2>
        )}
        <div className="flex items-center justify-center p-8">
          <p className="text-slate-500 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {showTitle && (
        <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">
          Multi-Factor Authentication
        </h2>
      )}
      <div className={`mt-5 space-y-6 ${!showTitle ? 'mt-0' : ''}`}>
        
        {/* Authenticator App */}
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                <FaShield className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  Authenticator App
                </h3>
                {user.mfa?.totp_enabled ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Set up on {new Date(user.mfa.totp_setup_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Not set up
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {user.mfa?.totp_enabled ? (
                <SecondaryButton
                  onClick={handleDeleteTOTP}
                  disabled={deletingTOTP}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                >
                  {deletingTOTP ? "Deleting..." : "Delete"}
                </SecondaryButton>
              ) : (
                      <PrimaryButton
                        onClick={(e) => {
                          e.preventDefault();
                          setTotpModalOpen(true);
                        }}
                      >
                        Set Up
                      </PrimaryButton>
              )}
            </div>
          </div>
        </div>

        {/* WebAuthn / Passkey */}
        {/* <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                <FaFingerprint className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  Passkey
                </h3>
                {user.mfa?.webauthn_enabled && user.mfa?.webauthn_credentials?.length > 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {user.mfa.webauthn_credentials.length} passkey{user.mfa.webauthn_credentials.length > 1 ? 's' : ''} set up
                    {user.mfa.webauthn_setup_date && (
                      <span className="block">
                        First set up on {new Date(user.mfa.webauthn_setup_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Not set up
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {user.mfa?.webauthn_enabled && user.mfa?.webauthn_credentials?.length > 0 ? (
                <SecondaryButton
                  onClick={handleManageWebauthn}
                >
                  Manage
                </SecondaryButton>
              ) : (
                <PrimaryButton
                  onClick={(e) => {
                    e.preventDefault();
                    setWebauthnModalOpen(true);
                  }}
                >
                  Set Up
                </PrimaryButton>
              )}
            </div>
          </div>
        </div> */}

        {/* Backup Codes */}
        <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-lg flex items-center justify-center">
                <FaKey className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  Backup Codes
                </h3>
                {user.mfa?.backup_codes && user.mfa.backup_codes.length > 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {user.mfa.backup_codes.length} codes available
                  </p>
                ) : (
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    No backup codes generated
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <SecondaryButton
                onClick={(e) => {
                  e.preventDefault();
                  setBackupCodesModalOpen(true);
                }}
              >
                {user.mfa?.backup_codes && user.mfa.backup_codes.length > 0 ? "Regenerate" : "Generate"}
              </SecondaryButton>
            </div>
          </div>
          {user.mfa?.backup_codes && user.mfa.backup_codes.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                <strong>Warning:</strong> Regenerating backup codes will permanently delete all existing codes. 
                Make sure you have saved your current codes before proceeding.
              </p>
            </div>
          )}
        </div>

      </div>
      
      {/* TOTP Setup Modal */}
      <TOTPSetupModal
        isOpen={totpModalOpen}
        onClose={() => setTotpModalOpen(false)}
        onSuccess={() => {
          setTotpModalOpen(false);
          // User data is automatically updated by TOTPSetupModal after verification
          dispatch(
            addToast({
              type: "success",
              message: "Authenticator app set up successfully!",
              timer: 5,
            })
          );
        }}
      />
      
      {/* Backup Codes Modal */}
      <BackupCodesModal
        isOpen={backupCodesModalOpen}
        onClose={() => setBackupCodesModalOpen(false)}
        onSuccess={() => {
          setBackupCodesModalOpen(false);
          // User data is automatically refreshed by BackupCodesModal after generation
          dispatch(
            addToast({
              type: "success",
              message: "Backup codes updated successfully!",
              timer: 5,
            })
          );
        }}
      />

      {/* WebAuthn Setup Modal */}
      <WebAuthnSetupModal
        isOpen={webauthnModalOpen}
        onClose={() => setWebauthnModalOpen(false)}
        onSuccess={() => {
          setWebauthnModalOpen(false);
          // User data is automatically updated by WebAuthnSetupModal after verification
          // No toast needed - user will see the updated UI showing the new passkey
        }}
      />

      {/* WebAuthn Manage Modal */}
      <WebAuthnManageModal
        isOpen={webauthnManageModalOpen}
        onClose={() => setWebauthnManageModalOpen(false)}
        onAddCredential={() => {
          setWebauthnManageModalOpen(false);
          setWebauthnModalOpen(true);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ModalBasic
        id="confirm-delete-totp-modal"
        modalOpen={confirmDeleteModalOpen}
        setModalOpen={setConfirmDeleteModalOpen}
        title="Delete Authenticator App"
        width='lg'
      >
        <div className="px-6 py-4">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                <FaTriangleExclamation className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                Are you sure?
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                This will permanently delete your authenticator app setup. You'll need to set it up again to re-enable MFA.
              </p>
              <div className="flex justify-end space-x-3">
                <SecondaryButton
                  onClick={() => setConfirmDeleteModalOpen(false)}
                  disabled={deletingTOTP}
                >
                  Cancel
                </SecondaryButton>
                <DangerButton
                  onClick={confirmDeleteTOTP}
                  disabled={deletingTOTP}
                >
                  {deletingTOTP ? "Deleting..." : "Delete"}
                </DangerButton>
              </div>
            </div>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
}

export default MFASection;

